import { useContext } from 'react';
import { noop } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { ModalName } from 'app/enums';
import { modalsReducers } from 'app/store/modal/slice';
import { calculateBuyAmountToSwapToCoverPlatformFeeCurrency } from 'shared/utils';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { UserBalanceValidationResult, validateUserBalance } from 'modules/payments/validators';
import { GameActivityPaymentContext } from 'modules/game-payments/providers';
import { MultiCurrencyConfig } from 'config/multi-currency-config';

interface EntryValidationResult {
  canEnter: boolean;
  canEnterWithReward: boolean;
  needSwap: boolean;
  openSwapModal: () => void;
}

const NO_OPTIONS_RESULT: EntryValidationResult = {
  canEnter: false,
  canEnterWithReward: false,
  needSwap: false,
  openSwapModal: noop,
};

const VALID_ENTRY_RESULT: EntryValidationResult = {
  canEnter: true,
  canEnterWithReward: false,
  needSwap: false,
  openSwapModal: noop,
};

export const useValidateUserEntry = (entryFee: number | undefined): EntryValidationResult => {
  const { paymentConfig, platformFeePercentage } = useContext(GameActivityPaymentContext);
  const dispatch = useDispatch();
  const userAccounts = useSelector(userPaymentAccountsSelector.all);

  const userMainAccount = userAccounts.find(
    (account) => account?.currency === paymentConfig.mainCurrency
  );
  const userPlatformFeeAccount = userAccounts.find(
    (account) => account?.currency === paymentConfig.platformFeeCurrency
  );

  if (!userMainAccount || !userPlatformFeeAccount) {
    return NO_OPTIONS_RESULT;
  }

  if (entryFee === undefined) return NO_OPTIONS_RESULT;
  if (entryFee === 0) return VALID_ENTRY_RESULT;

  const canEnterWithReward = Boolean(
    userMainAccount?.rewardBalance && entryFee <= userMainAccount?.rewardBalance
  );

  const userBalanceValidationResult = validateUserBalance(entryFee, {
    getUserAccounts: () => userAccounts,
    getExchangeRate: () => paymentConfig.exchangeRate,
    getPlatformFeePercentage: () => platformFeePercentage,
  });

  const getRegularBalanceResultIfNotEnoughBalance = () => {
    switch (userBalanceValidationResult.details?.currency) {
      case paymentConfig.mainCurrency: {
        return NO_OPTIONS_RESULT;
      }
      case paymentConfig.platformFeeCurrency: {
        if (!MultiCurrencyConfig.isSwapEnabled()) {
          return NO_OPTIONS_RESULT;
        }

        const { hasEnoughBalance, suggestedSwapBuyAmount } =
          calculateBuyAmountToSwapToCoverPlatformFeeCurrency({
            mainCurrencyAmount: entryFee,
            platformFeeCurrencyBalance: userPlatformFeeAccount.balance,
            mainCurrencyBalance: userMainAccount.balance,
            exchangeRate: paymentConfig.exchangeRate,
            gameActivityFeePercentage: platformFeePercentage,
          });

        if (!hasEnoughBalance) {
          return NO_OPTIONS_RESULT;
        }

        return {
          canEnter: true,
          needSwap: true,
          canEnterWithReward,
          openSwapModal: () =>
            dispatch(
              modalsReducers.openModal({
                name: ModalName.BalanceSwap,
                data: suggestedSwapBuyAmount,
              })
            ),
        };
      }
      default:
        return NO_OPTIONS_RESULT;
    }
  };

  let regularBalanceResult: EntryValidationResult;
  switch (userBalanceValidationResult.result) {
    case UserBalanceValidationResult.Valid: {
      regularBalanceResult = VALID_ENTRY_RESULT;
      break;
    }
    case UserBalanceValidationResult.NotEnoughBalance: {
      regularBalanceResult = getRegularBalanceResultIfNotEnoughBalance();
      break;
    }
    default: {
      return NO_OPTIONS_RESULT;
    }
  }

  return { ...regularBalanceResult, canEnterWithReward };
};
